// Phase 3: Loans module theme token extensions (light/dark aware placeholders)
// Integrates multi-market & currency color tokens into Ant Design v5 ConfigProvider theme.
// Future: Move dynamic tenant brand integration & market-accent derivation here.

import type { ThemeConfig } from 'antd';

export const loansTokens = {
    // Currency colors
    colorGBP: '#1890ff',
    colorEUR: '#13c2c2',
    colorUSD: '#52c41a',
    colorLBP: '#fa8c16',

    // Market colors (could diverge later for branding)
    colorMarketUK: '#1890ff',
    colorMarketEU: '#13c2c2',
    colorMarketLB: '#fa8c16',

    // Status colors (align with pipeline + account lifecycle)
    colorApplicationDraft: '#d9d9d9',
    colorApplicationSubmitted: '#1890ff',
    colorApplicationInReview: '#2f54eb',
    colorApplicationApproved: '#52c41a',
    colorApplicationDeclined: '#ff4d4f',
    colorApplicationConditions: '#faad14',
    colorApplicationDisbursed: '#52c41a',
    colorApplicationWithdrawn: '#d9d9d9',

    colorAccountCurrent: '#52c41a',
    colorAccountArrears: '#faad14',
    colorAccountDefault: '#ff4d4f'
} as const;

export type LoansTokenKeys = keyof typeof loansTokens;

/**
 * Injects loans-specific tokens into antd ThemeConfig.
 * Usage (example in a higher-level ThemeProvider wrapper):
 *
 *   const themeConfig = extendLoansTheme({
 *     token: { colorPrimary: '#1890ff' }, // existing app tokens
 *   }, currentMode);
 *
 *   <ConfigProvider theme={themeConfig}>...</ConfigProvider>
 */
export function extendLoansTheme(base: ThemeConfig = {}, mode: 'light' | 'dark' = 'light'): ThemeConfig {
    // Potential future differentiation for dark mode adjustments:
    const darkAdjustments: Partial<Record<LoansTokenKeys, string>> = mode === 'dark'
        ? {
            colorApplicationDraft: '#595959',
            colorApplicationSubmitted: '#177ddc',
            colorApplicationInReview: '#1d39c4',
            colorApplicationApproved: '#389e0d',
            colorApplicationDeclined: '#a61d24',
            colorApplicationConditions: '#d48806',
            colorApplicationDisbursed: '#389e0d',
            colorApplicationWithdrawn: '#434343',
            colorAccountCurrent: '#389e0d',
            colorAccountArrears: '#d48806',
            colorAccountDefault: '#a61d24'
        }
        : {};

    const mergedLoansTokens = {
        ...loansTokens,
        ...darkAdjustments
    };

    return {
        ...base,
        token: {
            ...base.token,
            // Expose loans tokens inside token namespace (prefixed optional)
            ...mergedLoansTokens
        },
        components: {
            ...(base.components || {}),
            // Example component-level overrides (placeholder):
            Tag: {
                // Could map status → specific background colors later
            },
            Badge: {
                // Placeholder for future currency / market color integration
            }
        }
    };
}

/**
 * Helper to map application status to a semantic color token.
 * Centralizes mapping so future theming changes occur in one place.
 */
export function mapApplicationStatusToColor(status: string, themeTokens?: Record<string, any>): string {
    switch (status) {
        case 'draft':
            return themeTokens?.colorApplicationDraft ?? loansTokens.colorApplicationDraft;
        case 'submitted':
            return themeTokens?.colorApplicationSubmitted ?? loansTokens.colorApplicationSubmitted;
        case 'inReview':
            return themeTokens?.colorApplicationInReview ?? loansTokens.colorApplicationInReview;
        case 'approved':
            return themeTokens?.colorApplicationApproved ?? loansTokens.colorApplicationApproved;
        case 'declined':
            return themeTokens?.colorApplicationDeclined ?? loansTokens.colorApplicationDeclined;
        case 'conditions':
            return themeTokens?.colorApplicationConditions ?? loansTokens.colorApplicationConditions;
        case 'disbursed':
            return themeTokens?.colorApplicationDisbursed ?? loansTokens.colorApplicationDisbursed;
        case 'withdrawn':
            return themeTokens?.colorApplicationWithdrawn ?? loansTokens.colorApplicationWithdrawn;
        default:
            return themeTokens?.colorApplicationDraft ?? loansTokens.colorApplicationDraft;
    }
}

export function mapAccountStatusToColor(status: string, themeTokens?: Record<string, any>): string {
    switch (status) {
        case 'pendingDisbursement':
            return themeTokens?.colorApplicationSubmitted ?? loansTokens.colorApplicationSubmitted;
        case 'active':
            return themeTokens?.colorAccountCurrent ?? loansTokens.colorAccountCurrent;
        case 'inArrears':
            return themeTokens?.colorAccountArrears ?? loansTokens.colorAccountArrears;
        case 'defaulted':
            return themeTokens?.colorAccountDefault ?? loansTokens.colorAccountDefault;
        case 'closed':
            return themeTokens?.colorApplicationWithdrawn ?? loansTokens.colorApplicationWithdrawn;
        case 'settled':
            return themeTokens?.colorApplicationApproved ?? loansTokens.colorApplicationApproved;
        default:
            return themeTokens?.colorApplicationDraft ?? loansTokens.colorApplicationDraft;
    }
}

/**
 * Placeholder future: currency accent chip background mapping.
 */
export function mapCurrencyToColor(code: string, themeTokens?: Record<string, any>): string | undefined {
    const key = `color${code.toUpperCase()}` as LoansTokenKeys;
    return themeTokens?.[key] ?? (loansTokens as any)[key];
}

// TODO (Phase 4/5):
// - Introduce CSS variables export for cross-system usage.
// - Provide motion token alignment (Framer Motion variants referencing tokenized durations/easings).
// - Integrate with SmartCard accent borders (market-aware).
